import { PlatformEventOfType, type PlatformEvent, type PlatformEventListener, type PlatformEventType } from "./events";

import { createContext, type PropsWithChildren, useContext, useEffect, useMemo, useRef } from "react";

export interface PlatformBridge {
  subscribe<TType extends PlatformEventType>(type: TType, listener: PlatformEventListener<TType>): () => void;
  dispatch(event: PlatformEvent): void;
}

type UntypedListener = (event: PlatformEvent) => void;

type ListenerMap = Map<PlatformEventType, Set<UntypedListener>>;
type LatestEventMap = Map<PlatformEventType, PlatformEvent>;

/**
 * Factory creating an in-memory bridge instance.
 */
export function createPlatformBridge(): PlatformBridge {
  const listenerMap: ListenerMap = new Map();
  const latestEvents: LatestEventMap = new Map();

  return {
    subscribe(type, listener) {
      let listeners = listenerMap.get(type);
      if (!listeners) {
        listeners = new Set();
        listenerMap.set(type, listeners);
      }

      listeners.add(listener as UntypedListener);

      const latest = latestEvents.get(type);
      if (latest) {
        // replay the latest event to new subscribers if available
        (listener as UntypedListener)(latest);
      }

      return () => {
        listeners.delete(listener as UntypedListener);

        if (listeners.size === 0) {
          listenerMap.delete(type);
        }
      };
    },

    dispatch(event) {
      latestEvents.set(event.type, event);

      const listeners = listenerMap.get(event.type);
      if (!listeners) return;

      listeners.forEach((listener) => {
        listener(event);
      });
    },
  };
}

const PlatformBridgeContext = createContext<PlatformBridge | null>(null);

export interface PlatformBridgeProviderProps extends PropsWithChildren {
  bridge: PlatformBridge;
}

export function PlatformBridgeProvider({ bridge, children }: PlatformBridgeProviderProps) {
  return <PlatformBridgeContext.Provider value={bridge}>{children}</PlatformBridgeContext.Provider>;
}

/**
 * Returns the current platform bridge from context.
 *
 * @throws When the hook is used outside of `PlatformBridgeProvider`.
 */
export function usePlatformBridge(): PlatformBridge {
  const bridge = useContext(PlatformBridgeContext);
  if (!bridge) {
    throw new Error("PlatformBridgeContext is undefined. Wrap components with PlatformBridgeProvider.");
  }
  return bridge;
}

type PlatformEventHandlers = Partial<{
  [K in PlatformEventType]: PlatformEventListener<K>;
}>;

/**
 * Subscribes to a set of platform events within a React component.
 */
export function usePlatformEvents(handlers: PlatformEventHandlers): void {
  const bridge = usePlatformBridge();
  const handlersRef = useRef(handlers);

  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const previousActiveTypesRef = useRef<PlatformEventType[]>([]);

  /**
   * filters out undefined subscribers and memoizes the resulting array to keep a stable reference and
   * avoid resubscribing on every render
   */
  const activeTypes = useMemo(() => {
    const next = Object.entries(handlers)
      .filter(([, handler]) => typeof handler === "function")
      .map(([type]) => type as PlatformEventType)
      .sort();

    const prev = previousActiveTypesRef.current;
    if (prev.length === next.length && prev.every((value, index) => value === next[index])) {
      return prev;
    }

    previousActiveTypesRef.current = next;
    return next;
  }, [handlers]);

  /**
   * Subscription management effect.
   */
  useEffect(() => {
    const disposers = activeTypes.map((type) =>
      bridge.subscribe(type, (event) => {
        const handler = handlersRef.current[type];

        if (typeof handler === "function") {
          handler(event as never);
        }
      }),
    );

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }, [bridge, activeTypes]);
}

/**
 * Subscribes to a single platform event inside a React component.
 */
export function usePlatformEvent<TType extends PlatformEventType>(
  type: TType,
  listener: PlatformEventListener<TType>,
): void {
  const bridge = usePlatformBridge();
  const listenerRef = useRef(listener);

  useEffect(() => {
    listenerRef.current = listener;
  }, [listener]);

  useEffect(
    () =>
      bridge.subscribe(type, (event) => {
        listenerRef.current(event as PlatformEventOfType<TType>);
      }),
    [bridge, type],
  );
}
